import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled"
import { Box, Typography } from "@mui/material"
import { KeyboardEvent, ReactElement } from "react"

import { type IVideo as IDnoVideo } from "./carouselLogic/videoCarouselFaqDno"
import { type IVideo as IInstallerVideo } from "./carouselLogic/videoCarouselFaqInstaller"

export type IVideo = IInstallerVideo | IDnoVideo

interface VideoTileProps {
    video: IVideo
    visibleTiles: number
    onClick: (video: IVideo) => void
}

const VideoTile = ({
    video,
    visibleTiles,
    onClick,
}: VideoTileProps): ReactElement => {
    const isClickable = Boolean(video.id)
    const thumbnailUrl = isClickable
        ? `https://img.youtube.com/vi/${video.id}/sddefault.jpg`
        : ""
    const tileWidth = `${100 / visibleTiles}%`

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
        if (!isClickable) return
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault()
            onClick(video)
        }
    }

    return (
        <Box
            sx={{
                minWidth: tileWidth,
                flex: `0 0 ${tileWidth}`,
                boxSizing: "border-box",
                padding: 1,
            }}
        >
            <Box
                onClick={() => isClickable && onClick(video)}
                onKeyDown={handleKeyDown}
                role={isClickable ? "button" : undefined}
                tabIndex={isClickable ? 0 : -1}
                aria-label={
                    isClickable ? `Open video ${video.title}` : undefined
                }
                sx={{
                    width: "100%",
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    cursor: isClickable ? "pointer" : "default",
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "flex-start",
                    boxShadow: 1,
                    transition: "transform 150ms ease",
                    overflow: "hidden",
                    "&:hover": {
                        transform: isClickable ? "translateY(-2px)" : "none",
                    },
                }}
            >
                <Box
                    sx={{
                        position: "relative",
                        width: "100%",
                        aspectRatio: "16 / 9",
                        backgroundColor: "common.black",
                        backgroundImage: isClickable
                            ? `url(${thumbnailUrl})`
                            : "none",
                        backgroundSize: "cover",
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "center",
                    }}
                >
                    {isClickable ? (
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "rgba(0, 0, 0, 0)",
                                transition: "background-color 150ms ease",
                                "&:hover": {
                                    backgroundColor: "rgba(0, 0, 0, 0.3)",
                                },
                            }}
                        >
                            <PlayCircleFilledIcon
                                sx={{
                                    fontSize: 60,
                                    color: "white",
                                    opacity: 0.9,
                                    transition: "opacity 150ms ease",
                                }}
                            />
                        </Box>
                    ) : (
                        <Typography
                            variant="subtitle1"
                            color="text.disabled"
                            textAlign="center"
                            sx={{
                                px: 1,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                height: "100%",
                            }}
                        >
                            Video coming soon
                        </Typography>
                    )}
                </Box>
            </Box>
        </Box>
    )
}

export default VideoTile
